import {APP_INITIALIZER, Injectable, LOCALE_ID, Provider} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {registerLocaleData} from '@angular/common';
import en from '@angular/common/locales/en';
import fi from '@angular/common/locales/fi';

registerLocaleData(en);
registerLocaleData(fi);

export interface SupportedLanguage {
  key: string;
  isDefault: boolean;
}

export const SUPPORTED_LANGUAGES: Array<SupportedLanguage> = [
  {key: 'en', isDefault: true},
  {key: 'fi', isDefault: false},
];

export const defaultLanguage = (): SupportedLanguage => {
  return SUPPORTED_LANGUAGES!.find((language: SupportedLanguage) => language.isDefault)!;
};

export const findLanguage = (
  languageKey: string | null | undefined,
): SupportedLanguage | undefined => {
  return SUPPORTED_LANGUAGES!.find((language: SupportedLanguage) => language.key === languageKey);
};

export function localeServiceProviders(): Array<Provider> {
  return [
    {
      provide: APP_INITIALIZER,
      useFactory: setupInitialLocale,
      deps: [LocaleService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useFactory: (localeService: LocaleService) => localeService.getLocale(),
      deps: [LocaleService],
    },
  ];
}

function setupInitialLocale(localeService: LocaleService) {
  return () => localeService.setupInitialLocale();
}

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  private static LOCALE_STORAGE_KEY = 'pta_locale';
  public static SUPPORTED_LOCALES = SUPPORTED_LANGUAGES.map(lang => lang.key);
  private locale!: string;

  constructor(private translateService: TranslateService) {
    this.translateService.addLangs(LocaleService.SUPPORTED_LOCALES);
    this.translateService.setDefaultLang(LocaleService.SUPPORTED_LOCALES[0]);
    this.setupInitialLocale();
  }

  setupInitialLocale(): void {
    const localeFromStorage = localStorage.getItem(LocaleService.LOCALE_STORAGE_KEY);
    this.setLocale(localeFromStorage);
  }

  getLocale(): string {
    return this.locale;
  }

  setLocale(locale: string | null | undefined): void {
    if (findLanguage(locale)) {
      this.locale = locale!;
    } else {
      this.locale = defaultLanguage().key;
    }

    this.translateService.use(this.locale);
    localStorage.setItem(LocaleService.LOCALE_STORAGE_KEY, this.locale);
  }
}
